import { detectBrand } from '@/lib/brand';
import { hasModule } from '@/lib/modules';
import type { MeResponse } from '@sklad-sync/shared';
import { type ReactNode, useState } from 'react';
import { NakupBell } from './NakupBell';
import { Sidebar } from './Sidebar';

type Props = {
  user: MeResponse;
  onLogout: () => void;
  children: ReactNode;
};

export function Layout({ user, onLogout, children }: Props) {
  // Na mobilu je sidebar zasunutý mimo obrazovku a otevírá se hamburgerem;
  // od `lg` výš je pořád vidět a stav se ignoruje.
  const [menuOpen, setMenuOpen] = useState(false);
  const brand = detectBrand();
  // Zvoneček nákupu jen pro ty, kdo nakupují (právo „Přehled nákupu").
  const canNakup = hasModule(user.modules, 'vyroba_nakup');

  return (
    <div className="flex min-h-screen">
      <Sidebar user={user} onLogout={onLogout} open={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* Zatmavení pod vysunutým menu — jen na mobilu. */}
      {menuOpen && (
        <button
          type="button"
          aria-label="Zavřít menu"
          onClick={() => setMenuOpen(false)}
          className="fixed inset-0 z-30 bg-black/40 lg:hidden cursor-default"
        />
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-20 flex items-center gap-3 bg-surface border-b border-border px-4 py-3">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Otevřít menu"
            className="text-text-secondary hover:text-text cursor-pointer p-1 -ml-1"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <span className="font-bold text-text truncate">{brand.name}</span>
          {canNakup && (
            <div className="ml-auto">
              <NakupBell />
            </div>
          )}
        </header>

        {/* Horní lišta na počítači — zatím jen zvoneček nákupu (vlevo). */}
        {canNakup && (
          <div className="hidden lg:flex sticky top-0 z-20 justify-start bg-surface/90 backdrop-blur border-b border-border px-8 py-2.5">
            <NakupBell />
          </div>
        )}

        {/* overflow-x-clip (ne hidden) — stránka se nikdy neposouvá do strany, ale
            nevzniká scroll kontejner, takže sticky prvky uvnitř dál fungují.
            Široké tabulky mají vlastní vodorovný scroll ve své kartě. */}
        <main className="flex-1 min-w-0 w-full max-w-[1400px] overflow-x-clip px-4 sm:px-8 py-6">
          {children}
        </main>
      </div>
    </div>
  );
}
